import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatDialog, MatDialogModule } from '@angular/material/dialog'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { NbSelectModule } from '@nebular/theme'
import { Subject } from 'rxjs'
import { switchMap, takeUntil } from 'rxjs/operators'
import { EmailComponentType } from '../../interfaces/email-component.interface'
import { EmailTemplate, TemplateComponent, UpdateComponentsRequest } from '../../interfaces/email-template.interface'
import { EmailTheme } from '../../interfaces/email-theme.interface'
import { EmailComposerService } from '../../services/email-composer.service'
import { MjmlService } from '../../services/mjml.service'
import { ComponentPropertiesComponent } from './components/component-properties/component-properties.component'
import { SendTestEmailDialogComponent } from './components/send-test-email-dialog/send-test-email-dialog.component'

@Component({
  selector: 'ngx-template-editor',
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NbSelectModule, MatDialogModule, ComponentPropertiesComponent]
})
export class TemplateEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() templateId: number | null = null
  @Output() backToList = new EventEmitter<void>()

  private destroy$ = new Subject<void>()

  template: EmailTemplate | null = null
  selectedTheme: EmailTheme | null = null
  availableComponents: EmailComponentType[] = []
  availableThemes: EmailTheme[] = []
  selectedComponent: TemplateComponent | null = null

  loading = false
  saving = false
  // MJML rendering cache for component previews
  private mjmlCache = new Map<string, SafeHtml>()
  private inFlightRenders = new Set<string>()

  // Inline editing properties
  editingField: string | null = null
  hoveredField: string | null = null

  constructor(private emailComposerService: EmailComposerService, private sanitizer: DomSanitizer, private matDialog: MatDialog, private mjmlService: MjmlService) {}

  ngOnInit(): void {
    this.loadInitialData()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['templateId']) {
      this.loadInitialData()
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private loadInitialData(): void {
    this.loading = true

    // Load available components
    this.emailComposerService
      .getAvailableComponents()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.availableComponents = response.data
          this.emailComposerService.setAvailableComponents(response.data)
        },
        error: (error) => console.error('Error loading components:', error)
      })

    // Load available themes
    this.emailComposerService
      .getThemes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.availableThemes = response.data
          // Set default theme
          const defaultTheme = response.data.find((t) => t.is_default) || response.data[0]
          if (defaultTheme) {
            this.selectedTheme = defaultTheme
            this.emailComposerService.setSelectedTheme(defaultTheme)
          }
        },
        error: (error) => console.error('Error loading themes:', error)
      })

    // Load template if editing
    if (this.templateId) {
      this.emailComposerService
        .getTemplate(this.templateId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            // Ensure components array exists and clean variable_values to use only name keys
            this.template = {
              ...response.data,
              components: (response.data.components || []).map((component) => this.cleanComponentVariableValues(component))
            }
            this.loading = false
          },
          error: (error) => {
            console.error('Error loading template:', error)
            this.loading = false
          }
        })
    } else {
      // Create new template
      this.template = {
        name: 'New Template',
        subject: 'New Email Subject',
        description: '',
        is_active: true,
        components: []
      }
      this.loading = false
    }
  }

  onBackToList(): void {
    this.backToList.emit()
  }

  onThemeChange(theme: EmailTheme): void {
    this.selectedTheme = theme
    this.emailComposerService.setSelectedTheme(theme)
  }

  onSaveTemplate(): void {
    if (!this.template) return

    this.saving = true

    let saveObservable: any

    if (this.templateId) {
      // Always update template basic info first
      const templateUpdate = this.emailComposerService
        .updateTemplate(this.templateId, {
          name: this.template.name,
          subject: this.template.subject,
          description: this.template.description,
          is_active: this.template.is_active
        })
        .pipe(takeUntil(this.destroy$))

      // Then update components if any
      if (this.template.components.length > 0) {
        const componentsData: UpdateComponentsRequest = {
          components: this.template.components.map((component) => {
            const componentType = this.availableComponents.find((ct) => ct.slug === component.slug)
            const cleanedComponent = this.cleanComponentVariableValues(component)
            return {
              component_id: componentType?.id || component.id!,
              sort_order: component.sort_order,
              variable_values: cleanedComponent.variable_values || {},
              component_styles: component.component_styles || {}
            }
          })
        }

        // Chain the two updates: first template, then components
        saveObservable = templateUpdate.pipe(
          // After template update succeeds, update components
          switchMap(() => this.emailComposerService.updateComponentsToTemplate(this.templateId!, componentsData)),
          takeUntil(this.destroy$)
        )
      } else {
        saveObservable = templateUpdate
      }
    } else {
      // Create new template (include components if any)
      const templateData = {
        name: this.template.name,
        subject: this.template.subject,
        description: this.template.description,
        is_active: this.template.is_active,
        components: this.template.components.map((component) => this.cleanComponentVariableValues(component))
      }
      saveObservable = this.emailComposerService.createTemplate(templateData)
    }

    saveObservable.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        // Preserve existing template data and merge with response
        // This prevents resetting name/subject when only components are updated
        this.template = {
          ...this.template,
          ...response.data,
          components: response.data.components || this.template?.components || []
        }
        // Don't change templateId if it's already set (for updates)
        if (!this.templateId && response.data.id) {
          this.templateId = response.data.id
        }
        this.saving = false
        // Show success message
      },
      error: (error) => {
        console.error('Error saving template:', error)
        this.saving = false
        // Show error message
      }
    })
  }

  isEditing(): boolean {
    return !!this.templateId
  }

  // Component Library Methods
  getComponentCategories(): string[] {
    const categories = new Set<string>()
    this.availableComponents.forEach((component) => {
      if (component.category) {
        categories.add(component.category)
      }
    })
    return Array.from(categories)
  }

  getCategoryLabel(category: string): string {
    return category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  }

  getComponentsByCategory(category: string): EmailComponentType[] {
    return this.availableComponents.filter((component) => component.category === category)
  }

  getComponentIconPath(componentSlug: string): string {
    const iconPaths: Record<string, string> = {
      header:
        'M18,14H20V17H23V19H20V22H18V19H15V17H18V14M4,3H18A2,2 0 0,1 20,5V12.08C18.45,11.82 16.92,12.18 15.68,13H12V15H13.08C12.97,15.68 12.97,16.35 13.08,17H4V19H11.08C11.21,20.72 12.08,22.19 13.68,23H4A2,2 0 0,1 2,21V5C2,3.89 2.9,3 4,3M4,7V9H18V7H4M4,11V13H18V11H4Z',
      footer: 'M4,3C2.89,3 2,3.89 2,5V19A2,2 0 0,0 4,21H20A2,2 0 0,0 22,19V5C22,3.89 21.1,3 20,3H4M4,5H20V19H4V5M6,7V9H18V7H6M6,11V13H18V11H6M6,15V17H14V15H6Z',
      'call-to-action': 'M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z',
      'text-content': 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z',
      image: 'M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z',
      button: 'M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z',
      divider: 'M19,13H5V11H19V13Z',
      spacer:
        'M16,12A2,2 0 0,1 18,10A2,2 0 0,1 20,12A2,2 0 0,1 18,14A2,2 0 0,1 16,12M10,12A2,2 0 0,1 12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12M4,12A2,2 0 0,1 6,10A2,2 0 0,1 8,12A2,2 0 0,1 6,14A2,2 0 0,1 4,12Z'
    }
    return iconPaths[componentSlug] || 'M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,7V13H13V7H11M11,15V17H13V15H11Z'
  }

  // Drag and Drop Methods
  onComponentDragStart(event: DragEvent, component: EmailComponentType): void {
    if (event.dataTransfer) {
      event.dataTransfer.setData(
        'text/plain',
        JSON.stringify({
          type: 'new-component',
          componentType: component
        })
      )
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault()
    event.dataTransfer!.dropEffect = 'move'
  }

  onDrop(event: DragEvent): void {
    event.preventDefault()
    const data = event.dataTransfer?.getData('text/plain')
    if (data && this.template) {
      try {
        const dropData = JSON.parse(data)
        if (dropData.type === 'new-component') {
          this.addComponentToTemplate(dropData.componentType)
        }
      } catch (error) {
        console.error('Error parsing drop data:', error)
      }
    }
  }

  // Component Management Methods
  addComponentToTemplate(componentType: EmailComponentType): void {
    if (!this.template) {
      return
    }

    // Ensure components array exists
    if (!this.template.components) {
      this.template.components = []
    }

    // Initialize variable values with preview data or defaults - using name keys
    const variableValues: Record<string, any> = {}

    if (componentType.variables) {
      componentType.variables.forEach((variable) => {
        variableValues[variable.name] = componentType.preview_data?.[variable.name] || variable.default_value || ''
      })
    }

    const newComponent: TemplateComponent = {
      name: componentType.name,
      slug: componentType.slug,
      sort_order: this.template.components.length + 1,
      variable_values: variableValues,
      component_styles: {}
    }

    this.template.components.push(newComponent)

    // If template is saved (has ID), also add component via API
    if (this.templateId) {
      this.saveComponentToAPI(newComponent)
    }
  }

  private saveComponentToAPI(component: TemplateComponent): void {
    if (!this.templateId) return

    const componentData = {
      component_id: this.availableComponents.find((c) => c.name === component.name)?.id!,
      sort_order: component.sort_order,
      variable_values: component.variable_values,
      component_styles: component.component_styles
    }

    this.emailComposerService
      .addComponentToTemplate(this.templateId, componentData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Update component with returned data (like DB ID)
          if (response.data?.id) {
            component.id = response.data.id
          }
        },
        error: (error) => {
          console.error('Error adding component:', error)
          // Remove component from local array if API call failed
          const index = this.template.components?.indexOf(component)
          if (index !== undefined && index >= 0) {
            this.template.components?.splice(index, 1)
          }
        }
      })
  }

  moveComponentUp(index: number): void {
    if (!this.template?.components || index <= 0) return

    const components = this.template.components
    const temp = components[index]
    components[index] = components[index - 1]
    components[index - 1] = temp

    // Update sort orders
    this.updateComponentOrders()
  }

  moveComponentDown(index: number): void {
    if (!this.template?.components || index >= this.template.components.length - 1) return

    const components = this.template.components
    const temp = components[index]
    components[index] = components[index + 1]
    components[index + 1] = temp

    // Update sort orders
    this.updateComponentOrders()
  }

  removeComponent(index: number): void {
    if (!this.template?.components) return

    const removedComponent = this.template.components[index]

    this.template.components.splice(index, 1)
    this.updateComponentOrders()

    // Clear selection if removed component was selected
    if (this.selectedComponent && this.template.components.indexOf(this.selectedComponent) === -1) {
      this.selectedComponent = null
    }

    // If template is saved and component has ID, remove via API
    if (this.templateId && removedComponent.id) {
      this.removeComponentFromAPI(removedComponent)
    }
  }

  private removeComponentFromAPI(component: TemplateComponent): void {
    if (!this.templateId || !component.id) return

    // Use the specific DELETE endpoint for removing components from templates
    this.emailComposerService
      .removeComponentFromTemplate(this.templateId, component.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Component removed successfully
        },
        error: (error) => {
          console.error('Error removing component:', error)
          // Re-add component to local array if API call failed
          if (this.template.components) {
            this.template.components.push(component)
            this.updateComponentOrders()
          }
        }
      })
  }

  private updateComponentOrders(): void {
    if (!this.template?.components) return

    this.template.components.forEach((component, index) => {
      component.sort_order = index + 1
    })
  }

  // Rendering Methods using mjml-browser via service
  renderComponent(component: TemplateComponent): SafeHtml {
    const componentType = this.availableComponents.find((ct) => ct.slug === component.slug)
    if (!componentType) {
      return this.sanitizer.bypassSecurityTrustHtml(`<div class="component-error">Component type "${component.slug}" not found</div>`)
    }

    const mjmlContent = componentType.mjml_template || componentType.html_template || ''

    // If no MJML template, fallback to simple HTML template
    if (!mjmlContent) {
      return this.sanitizer.bypassSecurityTrustHtml('<div>No template available</div>')
    }

    // Build cache key based on slug, theme and variables
    const themeKey = this.selectedTheme?.id ?? 'default'
    const variablesKey = JSON.stringify(component.variable_values || {})
    const cacheKey = `${component.slug}_${themeKey}_${variablesKey}`

    if (this.mjmlCache.has(cacheKey)) {
      return this.mjmlCache.get(cacheKey)!
    }

    if (!this.inFlightRenders.has(cacheKey)) {
      this.inFlightRenders.add(cacheKey)
      // Trigger async render
      this.mjmlService
        .renderMjml(mjmlContent, this.selectedTheme, component.variable_values || {})
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (html) => {
            const safe = this.sanitizer.bypassSecurityTrustHtml(html)
            this.mjmlCache.set(cacheKey, safe)
          },
          error: (err) => {
            const errorHtml = `<div class=\"component-error\">${err.message || 'Failed to render component'}</div>`
            const safe = this.sanitizer.bypassSecurityTrustHtml(errorHtml)
            this.mjmlCache.set(cacheKey, safe)
          }
        })
        .add(() => {
          this.inFlightRenders.delete(cacheKey)
        })
    }

    // If render finished synchronously, return cached value now
    if (this.mjmlCache.has(cacheKey)) {
      return this.mjmlCache.get(cacheKey)!
    }

    // Loading placeholder
    return this.sanitizer.bypassSecurityTrustHtml(
      '<div style="display:flex;align-items:center;justify-content:center;height:120px;background:#f8fafc;color:#6b7280;">Rendering...</div>'
    )
  }

  onComponentPropertiesChange(updatedProperties: any): void {
    if (this.selectedComponent) {
      // Clean existing variable_values to only use name keys, then add new values
      const componentType = this.availableComponents.find((ct) => ct.slug === this.selectedComponent!.slug)
      const cleanVariableValues: Record<string, any> = {}

      // Rebuild variable_values using only name keys
      if (componentType?.variables) {
        componentType.variables.forEach((variable) => {
          // Use the updated value if available, otherwise use existing name key, otherwise use slug key as fallback
          cleanVariableValues[variable.name] =
            updatedProperties[variable.name] ??
            this.selectedComponent!.variable_values?.[variable.name] ??
            this.selectedComponent!.variable_values?.[variable.slug] ??
            (variable.default_value || '')
        })
      }

      // Update the component's variable values with only name keys
      this.selectedComponent.variable_values = cleanVariableValues
      this.selectedComponent.variables = { ...this.selectedComponent.variables }

      // Clear cache for this component to force re-render with new variable values
      this.clearComponentCache(this.selectedComponent)
    }
  }

  // Clean variable_values to use only name keys (remove slug keys)
  private cleanComponentVariableValues(component: TemplateComponent): TemplateComponent {
    const componentType = this.availableComponents.find((ct) => ct.slug === component.slug)
    if (!componentType?.variables || !component.variable_values) {
      return component
    }

    const cleanVariableValues: Record<string, any> = {}

    // Rebuild variable_values using only name keys
    componentType.variables.forEach((variable) => {
      // Use existing name key if available, otherwise use slug key as fallback
      cleanVariableValues[variable.name] = component.variable_values?.[variable.name] ?? component.variable_values?.[variable.slug] ?? (variable.default_value || '')
    })

    return {
      ...component,
      variable_values: cleanVariableValues
    }
  }

  // Clear cache for a specific component to force re-render with updated variable_values
  private clearComponentCache(component: TemplateComponent): void {
    const componentType = this.availableComponents.find((ct) => ct.slug === component.slug)
    if (componentType) {
      // Clear all cache entries for this component by finding keys that start with the component slug
      const keysToDelete: string[] = []
      this.mjmlCache.forEach((value, key) => {
        if (key.startsWith(component.slug + '_')) {
          keysToDelete.push(key)
        }
      })

      // Remove all matching cache entries
      keysToDelete.forEach((key) => {
        this.mjmlCache.delete(key)
        this.inFlightRenders.delete(key)
      })
    }
  }

  // Component Selection
  selectComponent(component: TemplateComponent): void {
    this.selectedComponent = component
  }

  // TrackBy Functions
  trackByComponent(index: number, component: TemplateComponent): any {
    return component.slug + '_' + index
  }

  // Inline Editing Methods
  startEditing(field: string): void {
    this.editingField = field
    // Use setTimeout to focus input after Angular renders it
    setTimeout(() => {
      const input = document.querySelector('.editable-field.editing input') as HTMLInputElement
      if (input) {
        input.focus()
        input.select()
      }
    })
  }

  stopEditing(): void {
    if (this.editingField && this.template && this.templateId) {
      // Auto-save when editing stops
      this.onSaveTemplate()
    }
    this.editingField = null
  }

  // Send test email flow
  onSendTestEmail(): void {
    if (!this.template || !this.templateId) return

    // Lazy inline dialog using Material Dialog
    const dialogRef = this.matDialog.open(SendTestEmailDialogComponent, {
      width: '400px',
      panelClass: 'matdialog',
      data: { email: '', subject: this.template.subject || '' }
    })

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result?: { email: string; subject: string }) => {
        if (!result) return
        const payload = {
          theme_id: this.selectedTheme?.id ?? null,
          email: result.email,
          subject: result.subject || this.template!.subject
        }
        this.emailComposerService
          .sendTestEmail(this.templateId!, payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {},
            error: (err) => {
              console.error('Failed to send test email', err)
            }
          })
      })
  }
  headerActionsText(): string {
    if (this.saving) {
      return 'Saving...'
    }
    return this.templateId ? 'Save' : 'Create'
  }
}
